import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import SessionTimeOutDailog from "../components/Page-Components/Dailog-Component/SessionTimeOutDailog/SessionTimeOutDailog";
import { useAuthLogout } from "./authLogout";

const INACTIVITY_LIMIT = 10 * 60 * 1000; // 10 min
const WARNING_TIME = 58 * 60 * 1000; // 58 min
const SESSION_LIMIT = 60 * 60 * 1000; // 1 hour

export default function SessionManager() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const inactivityTimer = useRef(null);
  const warningTimer = useRef(null);
  const sessionTimer = useRef(null);

  const [showWarning, setShowWarning] = useState(false);
  const { appLogout } = useAuthLogout();

  // reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimer.current);

    inactivityTimer.current = setTimeout(() => {
      appLogout();
    }, INACTIVITY_LIMIT);
  }, [appLogout]);

  // start all timers
  const startTimers = useCallback(() => {
    clearTimeout(warningTimer.current);
    clearTimeout(sessionTimer.current);

    warningTimer.current = setTimeout(() => {
      setShowWarning(true);
    }, WARNING_TIME);

    sessionTimer.current = setTimeout(() => {
      appLogout();
    }, SESSION_LIMIT);

    resetInactivityTimer();
  }, [appLogout, resetInactivityTimer]);

  // user clicks Stay Signed In
  const handleStaySignedIn = async () => {
    try {
      await getAccessTokenSilently({ ignoreCache: true }); // refresh token
      setShowWarning(false);
      startTimers();
    } catch (err) {
      console.error("Token refresh failed", err);
      appLogout();
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    startTimers();

    const events = [
      "click",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "touchmove",
      "focus",
    ];

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        resetInactivityTimer();
      }
    };

    events.forEach((event) =>
      window.addEventListener(event, resetInactivityTimer),
    );

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(inactivityTimer.current);
      clearTimeout(warningTimer.current);
      clearTimeout(sessionTimer.current);

      events.forEach((event) =>
        window.removeEventListener(event, resetInactivityTimer),
      );

      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, startTimers, resetInactivityTimer]);
  return (
    <SessionTimeOutDailog
      open={showWarning}
      onClose={() => setShowWarning(false)}
      onStaySignedIn={handleStaySignedIn}
      onSignOut={appLogout}
    />
  );
}
